import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { VxAvatarComponent } from '@ui/avatar/avatar.component';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxCalloutComponent } from '@ui/callout/callout.component';
import { VxSkeletonComponent } from '@ui/skeleton/skeleton.component';
import { Apollo, gql } from 'apollo-angular';
import { fullName, initials } from 'src/app/models/user';
import {
  Invitation,
  KratosAuthenticationService,
} from 'src/app/services/authentication/kratos.service';
import { LoadOrganizationAction } from 'src/app/store/organization/organization.actions';
import { OrganizationStateModule } from 'src/app/store/organization/organization.state.module';
import {
  LoadUserAction,
  LogoutAction,
  SelectOrganizationAction,
} from 'src/app/store/user/user.actions';
import { UserState } from 'src/app/store/user/user.state';
import { AuthHeaderComponent } from '../components/auth-header/auth-header.component';
import { AuthPanelComponent } from '../components/auth-panel/auth-panel.component';
import { AuthShellComponent } from '../components/auth-shell/auth-shell.component';

const JOIN_ORGANIZATION_MUTATION = gql`
  mutation JoinOrganization($token: String!) {
    joinOrganization(token: $token) {
      id
    }
  }
`;

/**
 * An invitation opened by someone who is signed in already (sign-up sends
 * them here): join with this account, or sign out and sign up anew.
 */
@Component({
  selector: 'app-page-join-organization',
  templateUrl: './join-organization.component.html',
  imports: [
    RouterModule,
    TranslocoModule,
    OrganizationStateModule,
    AuthHeaderComponent,
    AuthPanelComponent,
    AuthShellComponent,
    VxAvatarComponent,
    VxButtonComponent,
    VxCalloutComponent,
    VxSkeletonComponent,
  ],
})
export class JoinOrganizationPageComponent {
  private readonly auth = inject(KratosAuthenticationService);
  private readonly apollo = inject(Apollo);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly invitation = signal<Invitation | undefined>(undefined);

  /** The invitation's token is unknown or expired. */
  protected readonly invitationMissing = signal(false);

  protected readonly user = this.store.selectSignal(UserState.getUser);

  protected readonly userName = computed(() => fullName(this.user()));

  protected readonly userInitials = computed(() => initials(this.user()));

  protected readonly isAlreadyMember = computed(() => {
    const organizationId = this.invitation()?.organization.id;
    return !!this.user()?.memberships.some(
      membership => membership.organizationId === organizationId
    );
  });

  protected readonly isJoining = signal(false);

  protected readonly joinFailed = signal(false);

  constructor() {
    this.initInvitation();
  }

  private initInvitation(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.loadInvitation(params['token']));
  }

  private loadInvitation(token: string): void {
    this.invitationMissing.set(false);
    this.auth.getInvitation(token).subscribe({
      next: invitation => this.invitation.set(invitation),
      error: () => this.invitationMissing.set(true),
    });
    this.store.dispatch(new LoadUserAction());
  }

  protected join(): void {
    const invitation = this.invitation();
    if (!invitation || this.isJoining()) {
      return;
    }
    this.joinFailed.set(false);
    this.isJoining.set(true);
    this.apollo
      .mutate({
        mutation: JOIN_ORGANIZATION_MUTATION,
        variables: { token: invitation.token },
      })
      .subscribe({
        next: () => {
          this.store
            .dispatch([
              new SelectOrganizationAction(invitation.organization.id),
              new LoadUserAction(),
              new LoadOrganizationAction(),
            ])
            .subscribe(() => this.router.navigate(['/']));
        },
        error: () => {
          this.isJoining.set(false);
          this.joinFailed.set(true);
        },
      });
  }

  protected openOrganization(): void {
    const invitation = this.invitation();
    if (!invitation) {
      return;
    }
    this.store
      .dispatch(new SelectOrganizationAction(invitation.organization.id))
      .subscribe(() => this.router.navigate(['/']));
  }

  /** Signs out and opens the invitation's sign-up for another address. */
  protected useOtherAccount(): void {
    this.store.dispatch(
      new LogoutAction('/register', { token: this.invitation()?.token })
    );
  }
}
